import * as React from 'react';
import { Question, SidebarSimple, Lightning, Sparkle } from '@phosphor-icons/react';

import { Logo } from '../../components/logo';
import { Button } from '../../components/ui/button';
import { AccountDropdown, AccountDropdownProps } from './account-dropdown';

import { cn } from '@/lib/utils';
import { useBooleanFlagValue } from '@/lib/feature-flags';

interface HeaderNavProps extends React.ComponentPropsWithoutRef<'header'>, Omit<AccountDropdownProps, 'dir'> {
  isSideNavOpen?: boolean;
  onSideNavigationToggle: () => void;
  onHelpClick: () => void;
  onBrainfishNativeClick: () => void;
  onLogoNav: () => void;
  isHomePath?: boolean;
  showSuggestionAction?: boolean;
  suggestionAction?: React.ReactNode;
  onSuggestionActionClick?: () => void;
  showSidebarToggle?: boolean;
  isSuperAdmin?: boolean;
  isViewer?: boolean;
  /** Ref for the sidebar toggle button; used by layout to move focus here when sidebar is collapsed. */
  sidebarToggleRef?: React.Ref<HTMLButtonElement>;
}

export const HEADER_NAV_HEIGHT = 60;

export const HeaderNav = React.forwardRef<HTMLElement, HeaderNavProps>(function HeaderNav(
  {
    className,
    isSideNavOpen = false,
    onSideNavigationToggle,
    onHelpClick,
    onBrainfishNativeClick,
    accountName,
    accountPhoto,
    accountDropdownItems,
    teams,
    side,
    align,
    showSuggestionAction,
    suggestionAction,
    onSuggestionActionClick,
    onLogoNav,
    isHomePath = false,
    showSidebarToggle = true,
    sidebarToggleRef,
    isSuperAdmin,
    isViewer,
    ...props
  },
  ref,
) {
  const hasSuggestionAction = showSuggestionAction && !!suggestionAction && !!onSuggestionActionClick;
  const brainfishNativeEnabled = useBooleanFlagValue('platform.brainfish-native.enabled', false);
  const isHelpButtonEnabled = !isViewer;

  return (
    <header ref={ref} {...props} className={cn('px-4 py-3 relative', className)}>
      <nav aria-label="Account navigation" className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <a
            href="/"
            onClick={(e) => {
              const isPlainLeftClick = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

              if (isPlainLeftClick) {
                e.preventDefault();
                onLogoNav();
              }
            }}
            aria-current={isHomePath ? 'page' : undefined}
          >
            <Logo
              variant="full"
              color={isHomePath ? 'fill' : 'outline'}
              width={130}
              height={20}
              className="hidden md:block"
            />
            <Logo
              variant="mark"
              color={isHomePath ? 'fill' : 'outline'}
              width={37}
              height={20}
              className="block md:hidden"
            />
          </a>
          {showSidebarToggle && (
            <Button
              ref={sidebarToggleRef}
              size="icon"
              variant="ghost"
              aria-label="Toggle side navigation"
              className="[&_svg]:size-6"
              onClick={onSideNavigationToggle}
            >
              <SidebarSimple aria-hidden="true" weight={isSideNavOpen ? 'fill' : 'regular'} />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-4">
          {hasSuggestionAction && (
            <Button variant="suggestion" onClick={onSuggestionActionClick}>
              <Lightning weight="fill" aria-hidden="true" /> {suggestionAction}
            </Button>
          )}
          {isHelpButtonEnabled && (
            <Button size="icon" variant="ghost" aria-label="Open help" className="[&_svg]:size-6" onClick={onHelpClick}>
              <Question aria-hidden="true" />
            </Button>
          )}
          {brainfishNativeEnabled && (
            <Button
              size="icon"
              aria-label="Open Brainfish Native"
              className="[&_svg]:size-4 size-6 rounded-3xl bg-yellowfin-gradient hover:bg-yellowfin-gradient-reverse focus-visible:bg-yellowfin-gradient-reverse"
              style={
                {
                  cornerShape: 'squircle',
                } as React.CSSProperties
              }
              onClick={onBrainfishNativeClick}
            >
              <span className="relative">
                <Sparkle aria-hidden="true" weight="fill" color="white" className="relative z-10" />
                <Sparkle
                  aria-hidden="true"
                  weight="fill"
                  className="text-dark-800 opacity-20 absolute z-0 left-[1px] top-[1px]"
                />
              </span>
            </Button>
          )}
          <AccountDropdown
            accountName={accountName}
            accountPhoto={accountPhoto}
            accountDropdownItems={accountDropdownItems}
            teams={teams}
            side={side}
            align={align}
            isSuperAdmin={isSuperAdmin}
          />
        </div>
      </nav>
    </header>
  );
});
